import React from "react"
import useFeatureFlag from "services/featureFlags"
import { Form } from "sharedTypes"
import {
  Box, Checkbox, Flex, HStack, Stack, Divider, Text,
} from "@chakra-ui/react"
import EventTranslationText from "components/elements/EventTranslationText"
import WhatsAppIcon from "components/elements/WhatsAppIcon"
import formatRegistrationFields from "./RegistrationFields/formatRegistrationFields"
import FixedBlockInput, { FixedBlockInputType } from "./RegistrationFields/FixedBlockInput"

type Props = {
  form: Form
  enableNameAndEmailToggle?: boolean
  showTopDivider?: boolean
  mergeTagEventTitle?: string
}

const DynamicInputFields = ({
  form,
  enableNameAndEmailToggle,
  showTopDivider,
  mergeTagEventTitle,
}: Props) => {
  const { whatsappMessaging } = useFeatureFlag()
  const fields = formatRegistrationFields(form, enableNameAndEmailToggle, { whatsappMessaging })

  const enabledFields = (fieldsToEnable) => fieldsToEnable.filter(({ enabled }) => enabled)

  const enabledPersonalFields = enabledFields(fields.personal)
  const enabledAddressFields = enabledFields(fields.address)

  if (enabledPersonalFields.length + enabledAddressFields.length === 0) {
    return null
  }

  const nameInputFields = (
    <Flex gap={4}>
      <Box flex={1}>
        <FixedBlockInput name={form.translations.firstName} required />
      </Box>
      <Box flex={1}>
        <FixedBlockInput name={form.translations.lastName} required />
      </Box>
    </Flex>
  )

  const whatsAppOptInPreview = (
    <Box key="profileWhatsappOptIn">
      <HStack align="flex-start" spacing={3}>
        <Checkbox
          isDisabled
          isChecked={false}
          colorScheme="blue"
          mt={1}
          aria-label="WhatsApp opt-in"
        />
        <Box flex={1}>
          <HStack spacing={2} align="center">
            <WhatsAppIcon size={4.5} color="#25D366" aria-hidden flexShrink={0} />
            <Text fontSize="sm" fontWeight="semibold">WhatsApp</Text>
          </HStack>
          <EventTranslationText
            text={form.translations.whatsappOptInDisclaimer}
            eventName={mergeTagEventTitle}
            fontSize="sm"
            mt={2}
          />
        </Box>
      </HStack>
    </Box>
  )

  return (
    <>
      {showTopDivider && <Divider my={4} />}
      <Stack px={6}>
        {enabledPersonalFields.map(({ fieldName, name, required }) => {
          switch (fieldName) {
            case "profileName":
              return (
                <React.Fragment key={fieldName}>
                  {nameInputFields}
                </React.Fragment>
              )

            case "profileWhatsappOptIn":
              return whatsAppOptInPreview

            case "profileBirthdate":
              return (
                <FixedBlockInput
                  key={name + fieldName}
                  name={name}
                  required={required}
                  type={FixedBlockInputType.Date}
                />
              )

            default:
              return (
                <FixedBlockInput
                  key={name + fieldName}
                  name={name}
                  required={required}
                />
              )
          }
        })}

        {enabledAddressFields.map(({ name, required, select }) => (
          <FixedBlockInput
            key={name}
            name={name}
            required={required}
            type={select ? FixedBlockInputType.Dropdown : FixedBlockInputType.Text}
          />
        ))}
      </Stack>
    </>
  )
}

export default DynamicInputFields
